import { environment } from '@environments/environment';
import { CurrencyPipe } from '@angular/common';
import { patch } from '@ngxs/store/operators';
import {
  ParticipantActions,
  ActivityState,
  ToastActions,
  Notification,
  ActivityActions,
  OrganizationState,
  ParticipantProduct,
  Payment,
  OrganizationActions,
  MemberActions,
} from '@app/_store';
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { from, Observable, of, switchMap } from 'rxjs';
import { AccountState } from '..';
import { ServerEventsActions } from './serverEvents.actions';
import { ServerEvent, ServerEventsStateModel } from './serverEvents.models';
import { Navigate } from '@ngxs/router-plugin';

@State<ServerEventsStateModel>({
  name: 'serverEvents',
  defaults: {
    isConnected: false,
  },
})
@Injectable()
export class ServerEventsState {
  signalR: HubConnection;
  signalRObs: Observable<void>;
  token: string | undefined;

  lockResolver: (value?: unknown) => void;

  constructor(
    private store: Store,
    private currencyPipe: CurrencyPipe
  ) {
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise(res => {
        this.lockResolver = res;
      });

      navigator.locks.request('unique_lock_name', { mode: 'shared' }, () => {
        return promise;
      });
    }

    this.store.select(AccountState.account).subscribe(account => {
      console.log('Configuring new token for next signalr reconnect');
      this.token = account?.token;
    });

    this.subscribeToReconnectedEvent();
  }

  @Selector()
  static connected(state: ServerEventsStateModel): boolean {
    return state?.isConnected || false;
  }

  subscribeToReconnectedEvent() {
    this.store.select(ServerEventsState.connected).subscribe(() => {
      if (this.signalR) {
        this.signalR.onreconnected(() => {
          if (this.store.selectSnapshot(OrganizationState.current)) {
            this.store.dispatch(new ServerEventsActions.SubscribeToOrganizationEvents());
          }
          const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);
          if (currentActivity) {
            this.store.dispatch(new ServerEventsActions.SubscribeToActivityEvents(currentActivity.id));
          }
        });
      }
    });
  }

  @Action(ServerEventsActions.CheckConnection)
  checkConnection() {
    this.store.dispatch(new ServerEventsActions.Connect());
  }

  @Action(ServerEventsActions.Connect)
  connect(ctx: StateContext<ServerEventsStateModel>): Observable<void> {
    if (
      this.signalR &&
      (this.signalR.state == HubConnectionState.Connecting ||
        this.signalR.state == HubConnectionState.Connected ||
        this.signalR.state == HubConnectionState.Reconnecting)
    ) {
      return this.signalRObs;
    }

    const onConnected = () => {
      console.log(`Connected to signalR with connectionId: ${this.signalR.connectionId}`);
      ctx.setState(
        patch<ServerEventsStateModel>({
          isConnected: true,
        })
      );
    };

    const account = this.store.selectSnapshot(AccountState.account);
    if (account && !this.signalR) {
      this.signalR = new HubConnectionBuilder()
        .withUrl(environment.notificationSignalrHubUrl, {
          accessTokenFactory: () => {
            return this.token;
          },
        })
        .withAutomaticReconnect()
        .build();

      this.signalR.on('publishEvent', this.onEventReceived);

      this.signalRObs = from(
        this.signalR
          .start()
          .then(() => onConnected())
          .catch(error => {
            console.error('SIGNALR ERROR:');
            console.error(error.message);
          })
      );
      return this.signalRObs;
    }

    return of();
  }

  @Action(ServerEventsActions.Disconnect)
  disconnect(ctx: StateContext<ServerEventsStateModel>): Observable<void> {
    this.lockResolver();
    const organization = this.store.selectSnapshot(OrganizationState.current);
    const activity = this.store.selectSnapshot(ActivityState.currentActivity);
    return ctx.dispatch(new ServerEventsActions.UnsubscribeFromActivityEvents(activity?.id)).pipe(
      switchMap(() => {
        return ctx.dispatch(new ServerEventsActions.UnsubscribeFromOrganizationEvents(organization?.id));
      }),
      switchMap(() => {
        if (!this.signalR) return of(null);
        return from(
          this.signalR.stop().finally(() => {
            this.signalR = undefined;
            ctx.setState(
              patch<ServerEventsStateModel>({
                isConnected: false,
              })
            );
          })
        );
      })
    );
  }

  @Action(ServerEventsActions.SubscribeToOrganizationEvents)
  subscribeToOrganizationEvents(): Observable<unknown> {
    console.log('Subscribing to organization events...');
    return from(
      this.signalR.invoke('subscribeToOrganizationEvents').then(() => {
        console.log('Subscribed to organization events!');
      })
    );
  }

  @Action(ServerEventsActions.UnsubscribeFromOrganizationEvents)
  unsubscribeFromOrganizationEvents(
    _ctx: StateContext<ServerEventsStateModel>,
    action: ServerEventsActions.UnsubscribeFromOrganizationEvents
  ): Observable<void> {
    if (action.organizationId && action.organizationId.length > 0) {
      console.log('Unsubscribing from organization events...');
      return from(
        this.signalR.invoke('unsubscribeFromOrganizationEvents', action.organizationId).then(() => {
          console.log('Unsubscribed from organization events!');
        })
      );
    } else {
      console.log('No organization to unsubscribe events from');
      return of(null);
    }
  }

  @Action(ServerEventsActions.SubscribeToActivityEvents)
  subscribeToActivityEvents(
    _ctx: StateContext<ServerEventsStateModel>,
    action: ServerEventsActions.SubscribeToActivityEvents
  ): Observable<void> {
    console.log('Subscribing to activity events...');
    return from(
      this.signalR.invoke('subscribeToActivityEvents', action.activityId).then(() => {
        console.log('Subscribed to activity events!');
      })
    );
  }

  @Action(ServerEventsActions.UnsubscribeFromActivityEvents)
  unsubscribeFromActivityEvents(
    _ctx: StateContext<ServerEventsStateModel>,
    action: ServerEventsActions.UnsubscribeFromActivityEvents
  ): Observable<void> {
    if (action.activityId) {
      console.log('Unsubscribing from activity events...');
      return from(
        this.signalR.invoke('unsubscribeFromActivityEvents', action.activityId).then(() => {
          console.log('Unsubscribed from activity events!');
        })
      );
    } else {
      console.log('No activity to unsubscribe events from');
      return of(null);
    }
  }

  onEventReceived = (event: ServerEvent<never>) => {
    console.log(`got event: ${event.eventType} with data ${JSON.stringify(event.data)}`);

    if (event.eventType == 'activity-link-state-change') {
      this.onActivityLinkStateChange(event);
    } else if (event.eventType == 'participant-added-product') {
      this.onParticipantAddedProduct(event);
    } else if (event.eventType == 'participant-removed-product') {
      this.onParticipantRemovedProduct(event);
    } else if (event.eventType == 'participant-updated-product') {
      this.onParticipantUpdatedProduct(event);
    } else if (event.eventType == 'participant-payment-completed') {
      this.onParticipantPaymentCompleted(event);
    } else if (event.eventType == 'activity-registration') {
      this.onActivityRegistration(event);
    } else if (event.eventType == 'sub-participant-added') {
      this.onActivitySubParticipantAdded(event);
    } else if (event.eventType == 'activity-auto-archived') {
      this.onActivityAutoArchived(event);
    } else if (event.eventType == 'products-delivered') {
      this.onProductsDelivered(event);
    } else if (event.eventType == 'payment-verified') {
      this.onPaymentVerified(event);
    } else if (event.eventType == 'organization-retired') {
      this.onOrganizationRetired(event);
    } else if (event.eventType == 'participant-integrationdata') {
      this.onParticipantIntegrationData(event);
    } else if (event.eventType == 'participant-integrationdata-error') {
      this.onParticipantIntegrationDataError(event);
    } else if (event.eventType == 'wallet-balance-updated') {
      this.onMemberWalletBalanceUpdated(event);
    }
  };

  onActivityLinkStateChange(event: ServerEvent<{ activityId: string; group: string; notification: Notification }>) {
    try {
      Notification.fixDates(event.data.notification);
      if (this.store.selectSnapshot(ActivityState.currentActivity).id !== event.data.activityId) return;
      this.store.dispatch(new ParticipantActions.UpdateOrAddNotification(event.data.group, event.data.notification));
    } catch (e) {
      console.log(e);
    }
  }

  onParticipantAddedProduct(
    event: ServerEvent<{
      activityId: string;
      activityName: string;
      participantId: string;
      participantName: string;
      product: ParticipantProduct;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ParticipantActions.AddProductFromServerEvent(event.data.participantId, event.data.product));
    }
    this.store.dispatch(
      new ToastActions.ShowUserEvent(
        `${event.data.participantName} lade till ${event.data.product.title} i aktiviteten ${event.data.activityName}`
      )
    );
  }

  onParticipantRemovedProduct(
    event: ServerEvent<{
      activityId: string;
      activityName: string;
      participantId: string;
      participantName: string;
      productId: string;
      productName: string;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ParticipantActions.RemoveProductFromServerEvent(event.data.participantId, event.data.productId));
    }
    this.store.dispatch(
      new ToastActions.ShowUserEvent(
        `${event.data.participantName} tog bort ${event.data.productName} i aktiviteten ${event.data.activityName}`
      )
    );
  }

  onParticipantUpdatedProduct(
    event: ServerEvent<{
      activityId: string;
      activityName: string;
      participantId: string;
      participantName: string;
      productName: string;
      product: ParticipantProduct;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ParticipantActions.UpdateProductFromServerEvent(event.data.participantId, event.data.product));
    }
    this.store.dispatch(
      new ToastActions.ShowUserEvent(
        `${event.data.participantName} gjorde ändringar på ${event.data.product.title} i aktiviteten ${event.data.activityName}`
      )
    );
  }

  onParticipantPaymentCompleted(
    event: ServerEvent<{
      activityId: string;
      activityName: string;
      participantId: string;
      participantName: string;
      payment: Payment;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ParticipantActions.AddPaymentFromServerEvent(event.data.participantId, event.data.payment));
    }
    this.store.dispatch(
      new ToastActions.ShowUserEvent(
        `${event.data.participantName} gjorde en betalning på ${this.currencyPipe.transform(
          event.data.payment.amount
        )} i aktiviteten ${event.data.activityName}`
      )
    );
  }

  onActivitySubParticipantAdded(
    event: ServerEvent<{
      activityId: string;
      activityName: string;
      participantName: string;
      subParticipantName: string;
      isAddedByParticipant: boolean;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ActivityActions.UpdateRequired(event.data.activityId));
    }

    if (event.data.isAddedByParticipant) {
      this.store.dispatch(
        new ToastActions.ShowUserEvent(
          `${event.data.participantName} lade till ${event.data.subParticipantName} i aktiviteten ${event.data.activityName}`
        )
      );
    } else {
      this.store.dispatch(
        new ToastActions.ShowUserEvent(
          `${event.data.subParticipantName} lades till via ${event.data.participantName}'s länk i aktiviteten ${event.data.activityName}`
        )
      );
    }
  }

  onActivityRegistration(
    event: ServerEvent<{ activityId: string; activityName: string; participantName: string; packageName: string }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(new ActivityActions.UpdateRequired(event.data.activityId));
    }
    this.store.dispatch(
      new ToastActions.ShowUserEvent(
        `${event.data.participantName} registrerade sig för paketet ${event.data.packageName} i aktiviteten ${event.data.activityName}`
      )
    );
  }

  onActivityAutoArchived(event: ServerEvent<{ activityName: string }>) {
    const organization = this.store.selectSnapshot(OrganizationState.current);

    this.store.dispatch(new ActivityActions.Init());

    console.log('dispatching toast');
    this.store.dispatch(
      new ToastActions.ShowUserEvent(`Aktiviteten ${event.data.activityName} har arkiverats (automatisk arkivering)`)
    );
    console.log('navigating()');

    this.store.dispatch(new Navigate(['/', 'organization', organization.id]));
  }

  onProductsDelivered(
    event: ServerEvent<{
      activityId: string;
      participantName: string;
      items: { participantId: string; productId: string }[];
      delivered: Date;
      deliveredBy: string;
      deliveredByUserId: string;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity).id === event.data.activityId) {
      this.store.dispatch(
        new ParticipantActions.UpdateDeliveryState(event.data.items, event.data.delivered, event.data.deliveredBy)
      );
    }

    if (this.store.selectSnapshot(AccountState.account).id != event.data.deliveredByUserId)
      this.store.dispatch(
        new ToastActions.ShowUserEvent(
          `${event.data.deliveredBy} markerade produkter som levererade till ${event.data.participantName}.`
        )
      );
  }

  onPaymentVerified(
    event: ServerEvent<{
      activityId: string;
      participantId: string;
      participantName: string;
      paymentId: string;
      verified: Date;
      verifiedBy: string;
      verifiedByUserId: string;
    }>
  ) {
    if (this.store.selectSnapshot(ActivityState.currentActivity)?.id === event.data.activityId) {
      this.store.dispatch(
        new ParticipantActions.UpdatePaymentVerification(
          event.data.participantId,
          event.data.paymentId,
          event.data.verified,
          event.data.verifiedBy
        )
      );
    }

    if (this.store.selectSnapshot(AccountState.account).id != event.data.verifiedByUserId)
      this.store.dispatch(
        new ToastActions.ShowUserEvent(
          `${event.data.verifiedBy} markerade en betalning från ${event.data.participantName} som kontrollerad.`
        )
      );
  }

  onOrganizationRetired(event: ServerEvent<{ organizationId: string; organizationName: string }>) {
    this.store.dispatch(
      new ToastActions.ShowSuccess(`Nerladdningsbara filer finns nu tillgängliga för ${event.data.organizationName}.`)
    );
    if (this.store.selectSnapshot(OrganizationState.current)?.id == event.data.organizationId) {
      this.store.dispatch(new OrganizationActions.LoadRetiredFiles());
    }
  }

  onParticipantIntegrationData(event: ServerEvent<{ participantId: string; integrationData: Record<string, string>[] }>) {
    this.store.dispatch(new ParticipantActions.UpdateIntegrationData(event.data.participantId, event.data.integrationData));
  }

  onParticipantIntegrationDataError(
    event: ServerEvent<{ participantId: string; error: string; integrationData: Record<string, string>[] }>
  ) {
    this.store.dispatch(new ParticipantActions.UpdateIntegrationData(event.data.participantId, event.data.integrationData));
    this.store.dispatch(new ToastActions.ShowError(event.data.error));
  }

  onMemberWalletBalanceUpdated(
    event: ServerEvent<{ organizationId: string; memberId: string; groupId: string; newBalance: number }>
  ) {
    this.store.dispatch(
      new MemberActions.UpdateWalletBalance(
        event.data.organizationId,
        event.data.memberId,
        event.data.groupId,
        event.data.newBalance
      )
    );
  }
}
